import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, PhoneIcon } from 'lucide-react';
const PHONE_LABEL = '+370 612 52240';
const PHONE_HREF = 'tel:+37061252240';
const INSTAGRAM_URL = 'https://www.instagram.com/vestirna/';
const FACEBOOK_URL = 'https://www.facebook.com/vestina.zablockyte';

/** Ta pati gama kaip navigacijos „Parašyk mums" mygtuke */
const INSTAGRAM_GRADIENT = 'linear-gradient(135deg, #F72585 0%, #7209B7 100%)';
const LAVENDER_BG = '#F3EBFC';
const LAVENDER_INK = '#2B1055';
const LAVENDER_MUTED = '#6C5C88';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 24
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
const tap = {
  whileHover: {
    scale: 1.03
  },
  whileTap: {
    scale: 0.97
  },
  transition: {
    duration: 0.15,
    ease: [0.23, 1, 0.32, 1] as [number, number, number, number]
  }
};

/** Vienodas visų mygtukų dydis */
const BUTTON_BASE = 'inline-flex items-center justify-center gap-2.5 w-full sm:w-auto sm:min-w-[200px] px-6 py-3.5 rounded-full text-sm font-semibold';
function InstagramGlyph({
  className = 'w-4 h-4'
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" data-id="element-2" data-mp="go5x8xol11y2">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" data-id="element-3" data-mp="ck6l0rac4pai" />
    </svg>;
}
function MessengerGlyph({
  className = 'w-4 h-4'
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="currentColor" data-id="element-4" data-mp="frfq3zr8djaq">
      <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.908 1.434 5.503 3.678 7.2V22l3.455-1.896A10.7 10.7 0 0012 20.486c5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.119 12.159L10.7 11.6l-4.7 2.6 5.166-5.484 2.482 2.559 4.636-2.559-5.165 5.443z" data-id="element-5" data-mp="kq0i5bwbwwo3" />
    </svg>;
}

/** Tęstinė kontaktų sekcija — prisišlieja prie kalendoriaus sekcijos aukščiau */
export function ContactSection() {
  return <section id="kontaktai" className="pt-4 pb-20 px-4 bg-sand-lightest" data-id="element-6" data-mp="2wf1kiynaeq60">
      <motion.div className="max-w-5xl mx-auto grid md:grid-cols-2 rounded-3xl overflow-hidden border border-sand-dark/25" {...fadeInUp} data-id="element-7" data-mp="2wh4j472i9rmc">
        {/* Paskambink */}
        <div className="bg-mountain-darker px-8 py-10 md:px-12 flex flex-col items-start" data-id="element-8" data-mp="2syf5pe2u6qhu">
          <p className="font-accent italic text-gold-light text-xl mb-1" data-id="element-9" data-mp="2o4uhueuyjcwd">
            Įdomu?
          </p>
          <h2 className="text-white font-bold text-2xl md:text-3xl mb-3" data-id="element-10" data-mp="2j7kfxzqdt8rn">
            Paskambink
          </h2>
          <p className="text-white/65 text-sm leading-relaxed mb-7" data-id="element-11" data-mp="2f3mkbz09mg3c">
            Greičiausias būdas išsiaiškinti, ar kursas tau. Atsakysime į visus
            klausimus apie kursą ir metodiką.
          </p>
          <motion.a href={PHONE_HREF} {...tap} className={`${BUTTON_BASE} bg-white text-mountain-darker mt-auto`} data-id="element-12" data-mp="36v2fihfw1b56">
            <PhoneIcon className="w-4 h-4 flex-shrink-0 text-gold-dark" data-id="element-13" data-mp="2xvp2jesk24fp" />
            {PHONE_LABEL}
          </motion.a>
        </div>

        {/* Parašyk žinutę */}
        <div className="px-8 py-10 md:px-12 flex flex-col items-start" style={{
        background: LAVENDER_BG
      }} data-id="element-14" data-mp="1mgijm687gnxd">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-4" style={{
          background: '#E9DBF8',
          color: '#7209B7'
        }} data-id="element-15" data-mp="3abn6s4h2tae9">
            <MessageCircleIcon className="w-5 h-5" data-id="element-16" data-mp="35edfeja4mons" />
          </span>
          <h3 className="font-bold text-2xl md:text-3xl mb-3" style={{
          color: LAVENDER_INK
        }} data-id="element-17" data-mp="35ckbhbqweijh">
            Parašyk žinutę
          </h3>
          <p className="text-sm leading-relaxed mb-7" style={{
          color: LAVENDER_MUTED
        }} data-id="element-18" data-mp="1vtwi7dc8v5n">
            Atsakome per kelias valandas — ir apie metodiką, ir apie datas.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-auto w-full sm:w-auto" data-id="element-19" data-mp="28nuo55wi4xq4">
            <motion.a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" {...tap} className={`${BUTTON_BASE} text-white`} style={{
            background: INSTAGRAM_GRADIENT
          }} data-id="element-20" data-mp="2dn7ew1qhuoxz">
              <InstagramGlyph data-id="element-21" data-mp="2f561gjx17ezu" />
              Instagram
            </motion.a>
            <motion.a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" {...tap} className={`${BUTTON_BASE} bg-white`} style={{
            color: LAVENDER_INK
          }} data-id="element-22" data-mp="2a5jn275gvspg">
              <MessengerGlyph className="w-4 h-4 text-[#0084FF]" data-id="element-23" data-mp="1hf1behqogj0f" />
              Messenger
            </motion.a>
          </div>
        </div>
      </motion.div>
    </section>;
}